import React, { useState, useMemo, useEffect } from 'react';
// Replace with your actual utility
import { ROUTES } from '../../common/constants/urls';
import { Box, Typography } from 'components/index';
import { Divider, Stack, Button, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import { CustomTooltip } from '../common/CustomTooltip';

const LIST_PAGE_SIZE = 50;
interface MapProjectsListProps {
  projects: any[];
  checkboxSelection?: boolean;
  onProjectsSelect?: (selectedProjectsIds: string[]) => void;
  disabledProjectsIds?: string[];
  hoveredProjectId?: string;
  setHoveredProjectId?: React.Dispatch<any>;
  loading?: boolean;
}

const MapProjectsList: React.FC<MapProjectsListProps> = ({
  projects,
  checkboxSelection,
  onProjectsSelect,
  disabledProjectsIds = [],
  hoveredProjectId,
  setHoveredProjectId,
  loading = false,
}) => {
  const [itemsToShow, setItemsToShow] = useState(LIST_PAGE_SIZE);
  const [selectedProjectIds, setSelectedProjectsIds] = useState<string[]>([]);

  useEffect(() => {
    setItemsToShow(LIST_PAGE_SIZE);
  }, [projects]);

  useEffect(() => {
    if (onProjectsSelect) {
      onProjectsSelect(selectedProjectIds);
    }
  }, [selectedProjectIds, onProjectsSelect]);

  const handleLoadMore = () => {
    setItemsToShow(prev => prev + LIST_PAGE_SIZE);
  };

  const handleSelectProject = (projectId: string) => {
    if (disabledProjectsIds.includes(projectId)) return;

    setSelectedProjectsIds(prev => {
      const isAlreadySelected = prev.includes(projectId);
      const updatedSelection = isAlreadySelected
        ? prev.filter(id => id !== projectId) // Deselect the project
        : [...prev, projectId]; // Select the project

      return updatedSelection;
    });
  };

  const paginatedProjects = useMemo(() => {
    return projects.slice(0, itemsToShow);
  }, [projects, itemsToShow]);

  const openProjectInNewTab = (projectId: string) => {
    window.open(`${ROUTES.PROJECTS}/${projectId}`, '_blank');
  };

  const getBgColor = (projectId: string) => {
    if (disabledProjectsIds.includes(projectId)) return '#f0f0f0';
    if (hoveredProjectId === projectId) return '#BFE0E8';
    return undefined;
  };

  const countStart = Math.min(1, projects.length);
  const countEnd = Math.min(itemsToShow, projects.length);

  const selectedProjectsText = selectedProjectIds.length > 0 ? `(${selectedProjectIds.length} selected)` : '';

  const allSelected = React.useMemo(() => {
    if (projects.length === 0) return false;
    return projects.filter(project => !disabledProjectsIds.includes(project.Id)).every(project => selectedProjectIds.includes(project.Id));
  }, [selectedProjectIds, projects, disabledProjectsIds]);

  const handleSelectAll = React.useCallback(() => {
    if (allSelected) {
      setSelectedProjectsIds([]);
    } else {
      const newSelectedIds = projects.filter(project => !disabledProjectsIds.includes(project.Id)).map(project => project.Id);
      setSelectedProjectsIds(newSelectedIds);
    }
  }, [allSelected, projects, disabledProjectsIds]);

  return (
    <Box sx={wrapperStyles}>
      <Box style={headerStyles}>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          {checkboxSelection && <FormControlLabel control={<Checkbox checked={allSelected} onChange={handleSelectAll} />} label='' />}
          <Typography variant='body1'>Results {selectedProjectsText}</Typography>
        </Stack>
        {!loading && (
          <Typography variant='body2'>
            {countStart}-{countEnd} of {projects.length}
          </Typography>
        )}
      </Box>
      {loading ? (
        <Typography variant='body2' textAlign='center' sx={{ mt: 2 }}>
          <CircularProgress />
        </Typography>
      ) : (
        <Box style={{ flex: 1, overflowY: 'auto', height: '100%', maxHeight: '100%' }}>
          {paginatedProjects.length > 0 ? (
            paginatedProjects.map(project => (
              <>
                <Stack
                  p={2}
                  width='100%'
                  key={project.Id}
                  spacing={1}
                  direction='row'
                  alignItems='flex-start'
                  onMouseOver={() => setHoveredProjectId && setHoveredProjectId(project.Id)}
                  onMouseOut={() => setHoveredProjectId && setHoveredProjectId(null)}
                  sx={{
                    backgroundColor: getBgColor(project.Id),
                  }}
                >
                  {checkboxSelection && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedProjectIds.includes(project.Id)}
                          onChange={() => handleSelectProject(project.Id)}
                          disabled={disabledProjectsIds.includes(project.Id)}
                        />
                      }
                      label=''
                      sx={{ display: 'flex', margin: 0 }}
                    />
                  )}
                  <Stack spacing={1}>
                    <Stack direction='row' alignItems='top' justifyContent='flex-start'>
                      <CustomTooltip title={`${project.ProjectNumber} - ${project.ProjectTitle}`}>
                        <Typography
                          variant='subtitle2'
                          fontWeight='bold'
                          maxWidth={project?.IsDraft ? '70%' : '100%'}
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
                          onClick={() => openProjectInNewTab(project.Id)}
                        >
                          {renderHighlightedText('ProjectNumber', project)} - {renderHighlightedText('ProjectTitle', project)}
                        </Typography>
                      </CustomTooltip>
                    </Stack>
                    {project.City && (
                      <Typography variant='caption' display='block'>
                        {renderHighlightedText('City', project)}
                      </Typography>
                    )}
                    {project.ProjectType && (
                      <Typography variant='caption' display='block'>
                        Project Type: {renderHighlightedText('ProjectType', project)}
                      </Typography>
                    )}
                    {project.Observer && (
                      <Typography variant='caption' display='block'>
                        Observer: {renderHighlightedText('Observer', project)}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                <Divider sx={{ margin: 0 }} />
              </>
            ))
          ) : (
            <Typography variant='body2' textAlign='center'>
              No projects to show
            </Typography>
          )}

          {itemsToShow < projects.length && (
            <div style={{ textAlign: 'center' }}>
              <Button variant='text' onClick={handleLoadMore}>
                Load More
              </Button>
            </div>
          )}
        </Box>
      )}
    </Box>
  );
};

const wrapperStyles = {
  width: '400px',
  height: '100%',
  maxHeight: '100%',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  background: 'white',
  boxShadow: 3,
  zIndex: 2,
};

const headerStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  padding: '16px',
};

const renderHighlightedText = (fieldName: string, project: any) => {
  const highlight = project['@search.highlights']?.[fieldName]?.[0] || project[fieldName];

  if (!highlight) return null;

  return (
    <span
      dangerouslySetInnerHTML={{
        __html: highlight.replaceAll(/<em>/g, '<em style="background-color: yellow; font-style: normal; padding: 0 2px;">'),
      }}
    />
  );
};

export default MapProjectsList;
